import { getStoreInstance } from 'store/configureStore';
import { setToken } from 'store/core/actions';

// Buffer: refresh when less than 5 minutes remain
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

let refreshInProgress = null;

/**
 * Returns a valid access token. If the current token is near expiry
 * and a refresh_token is available, silently refreshes first.
 */
export const getValidAccessToken = async () => {
  const store = getStoreInstance();
  if (!store) return null;

  const { token, refreshToken, expires, email } = store.getState().core;

  // If no token at all, nothing we can do
  if (!token) return null;

  // If token is not near expiry, return it
  if (expires && Date.now() < expires - REFRESH_BUFFER_MS) {
    return token;
  }

  // Token is expired or near expiry — try refreshing
  if (!refreshToken || !email) {
    return token; // No refresh_token available, return current (may fail with 401)
  }

  // Deduplicate concurrent refresh calls
  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = refreshAccessToken(store, email, refreshToken);
  try {
    return await refreshInProgress;
  } finally {
    refreshInProgress = null;
  }
};

async function refreshAccessToken(store, email, refreshToken) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_API_URL}/api/token`,
      {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `grant_type=refresh_token&email=${encodeURIComponent(email)}&refresh_token=${encodeURIComponent(refreshToken)}`,
      },
    );

    if (!response.ok) {
      return store.getState().core.token;
    }

    const json = await response.json();
    if (json.access_token) {
      const newExpires = json.expires_in * 1000 + Date.now();
      store.dispatch(
        setToken(json.access_token, newExpires, undefined, json.refresh_token),
      );
      return json.access_token;
    }
  } catch (e) {
    // Refresh failed — return current token as fallback
  }
  return store.getState().core.token;
}
